import { useCallback, useRef, useState } from 'react';
import { REASON_OVERRIDE_ERROR_MESSAGES, saveReasonOverride } from '../../services/matchingApi.js';
import { readReasonsOverride, withReasonsOverride } from './reasonsOverride';

/**
 * おすすめポイントの文の編集 (IN-M-012・BTN-M-015〜018) の状態と保存。
 * 画面レイアウト_SCR-M-001.md §6.2「おすすめポイントの文の編集」、Issue #777。
 *
 * useAiMatching から呼ぶ (マッチ度の保存〈useScoreAdjustments〉・求職者に提案するかの保存
 * 〈useCandidateVisibility〉と同じ分け方。作りはマッチ度の数字の書き換えにそろえた)。
 *
 * 編集中・保存中・失敗の文言は、どの推薦結果に対するものかを一緒に持つ。保存の間に表示する結果が
 * 替わったとき (保存中に実行した等)、前の結果の状態を新しい結果に出さないため (§6.2 手順 9)。
 * 保存は重ねて送らない。ほかの保存 (マッチ度・求職者に提案するか) の保存中も呼ばない
 * (同じ推薦結果の行をロックして書くため。§6.2「同時に保存するもの」)。保存中かは押した時点の
 * ref の値で判定する (useCandidateVisibility.js の説明)。
 *
 * マッチ度の編集と同時に開かないこと (§6.2 手順 1) は、呼び出し元が「編集」を押せなくして守る。
 *
 * @param {object|null} matchingResult 表示中の推薦結果
 * @param {Function} setMatchingResult
 * @param {Function} setOrderedItems 画面の並び (カードに渡す要素) の更新
 * @param {{current: boolean}|null} otherSavingRef ほかの保存中か (マッチ度・求職者に提案するか)
 * @param {{current: boolean}} savingRef 本フックの保存中を書き込む ref (ほかのフックが押した時点で読む)
 */
function useReasonOverrides(matchingResult, setMatchingResult, setOrderedItems, otherSavingRef, savingRef) {
    const shownResultId = matchingResult?.id ?? null;
    // 保存の応答を受け取った時点で表示中の結果を知るために持つ (useScoreAdjustments と同じ)
    const shownResultIdRef = useRef(shownResultId);
    shownResultIdRef.current = shownResultId;

    // 編集中の求人 ({ resultId, itemId })・保存中の求人 (同)・失敗の文言 ({ resultId, itemId, message })
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(null);
    const [failure, setFailure] = useState(null);

    const forShown = (state) => (state && state.resultId === shownResultId ? state : null);
    const editingForShown = forShown(editing);
    const savingForShown = forShown(saving);
    const failureForShown = forShown(failure);

    // 編集を始める (BTN-M-015)。編集できるカードは同時に 1 枚だけ
    const startReasonsEdit = useCallback(
        (jobPoolItemId) => {
            if (!matchingResult || savingRef.current || otherSavingRef?.current || editingForShown) return;
            setEditing({ resultId: matchingResult.id, itemId: jobPoolItemId });
            setFailure(null);
        },
        [matchingResult, savingRef, otherSavingRef, editingForShown]
    );

    // 編集をやめる (BTN-M-017。編集中に実行したときも呼ぶ)。保存中はやめられない。
    // 編集中でなければ何もしない (「AI の文に戻す」の失敗の文言を、実行で消さないため)
    const cancelReasonsEdit = useCallback(() => {
        if (savingRef.current || editing === null) return;
        setEditing(null);
        setFailure(null);
    }, [savingRef, editing]);

    const clearReasonsOverrideError = useCallback(() => setFailure(null), []);

    // 求人 1 件の文を保存する (BTN-M-016)。text が null なら編集した文を消す (BTN-M-018)。
    // 保存できたら true。画面の文は応答の値で入れ替える (送った文をそのまま使わない。§6.2 手順 5)
    const saveReasons = useCallback(
        async (jobPoolItemId, text) => {
            if (!matchingResult || savingRef.current || otherSavingRef?.current) return false;
            // 別のカードを編集している間は、ほかのカードの保存 (AI の文に戻す) を受け付けない
            if (editingForShown && editingForShown.itemId !== jobPoolItemId) return false;
            const targetId = matchingResult.id;
            savingRef.current = true;
            setSaving({ resultId: targetId, itemId: jobPoolItemId });
            setFailure(null);

            // 保存中の印は finally で戻す (戻らないと、以後すべての保存が押せなくなり、エラーとしても見えない)
            try {
                let result;
                try {
                    result = await saveReasonOverride(targetId, jobPoolItemId, text);
                } catch (err) {
                    // 失敗は saveReasonOverride が結果で返す。ここに来るのは想定外の例外だけ
                    console.error('useReasonOverrides: おすすめポイントの保存で例外が発生しました', err);
                    result = { ok: false, errorMessage: REASON_OVERRIDE_ERROR_MESSAGES.network };
                }

                if (result.ok) {
                    const savedOverride = readReasonsOverride(result.recommendation);
                    const applySaved = (rec) =>
                        rec.job_pool_item_id === jobPoolItemId ? withReasonsOverride(rec, savedOverride) : rec;
                    // 保存の間に別の結果へ替わっていたら入れない。並びは変えない (その求人の文だけを入れ替える)
                    setMatchingResult((prev) =>
                        prev && prev.id === targetId && Array.isArray(prev.recommendations)
                            ? { ...prev, recommendations: prev.recommendations.map(applySaved) }
                            : prev
                    );
                    if (shownResultIdRef.current === targetId) {
                        setOrderedItems((prev) => prev.map(applySaved));
                    }
                    setEditing((prev) =>
                        prev && prev.resultId === targetId && prev.itemId === jobPoolItemId ? null : prev
                    );
                } else {
                    setFailure({ resultId: targetId, itemId: jobPoolItemId, message: result.errorMessage });
                }
                return result.ok;
            } finally {
                savingRef.current = false;
                setSaving(null);
            }
        },
        [matchingResult, savingRef, otherSavingRef, editingForShown, setMatchingResult, setOrderedItems]
    );

    return {
        // どの結果の保存でも真 (保存を重ねて送らない。ほかの操作と PDF 出力を押せなくするのにも使う)
        reasonsOverrideSaving: saving !== null,
        // 表示中の結果で、編集中の求人・保存中の求人
        editingReasonsItemId: editingForShown ? editingForShown.itemId : null,
        savingReasonsItemId: savingForShown ? savingForShown.itemId : null,
        reasonsOverrideError: failureForShown
            ? { itemId: failureForShown.itemId, message: failureForShown.message }
            : null,
        startReasonsEdit,
        cancelReasonsEdit,
        clearReasonsOverrideError,
        saveReasons,
    };
}

export default useReasonOverrides;
